import _ from 'lodash';
import { t } from 'i18next';
import Numeral from 'numeral';
import { safeParseInt } from '../../../common/utils';
import { SearchFiltersType, TOptionsConfig, TSearchFilters } from './types';
import { getGameConfig } from '../../../gameConfig';
import { generateAllAffixPermutations } from '../../../common/affixHelpers/generateAllAffixPermutations';

export let OPTIONS_CONFIG: TOptionsConfig;

export const initOptionsConfig = (optionsConfig: TOptionsConfig) => {
  OPTIONS_CONFIG = optionsConfig;
  processFiltersToAssignIds(OPTIONS_CONFIG);
};

function minutesToTimeLabel(minutes: number) {
  const hours = Math.floor(minutes / 60);
  minutes -= hours * 60;
  return `${Numeral(hours).format('00')}:${Numeral(minutes).format('00')}`;
}

export const getHours = (type?: 'start' | 'end') => {
  const hours = _.flatten<{ label: string; value: number }>(
    _.range(0, 24).map((num: number) => [
      { label: minutesToTimeLabel(num * 60), value: num * 60 },
      { label: minutesToTimeLabel(num * 60 + 30), value: num * 60 + 30 }
    ])
  );

  if (type === 'end') {
    hours.shift();
    hours.push({ label: minutesToTimeLabel(1440), value: 0 });
  }

  return hours;
};

export function buildGlobalSearchQuery(type: string, allFilters: any, timezone?: string | null) {
  const queryObject = _.reduce(
    allFilters,
    (acc: any, filter: any) => {
      const { filterPath, value } = filter;
      if (!acc[filterPath]) {
        acc[filterPath] = [];
      }
      acc[filterPath].push(value);
      return acc;
    },
    { type }
  );

  // override it to ensure we are setting proper type - but we set it in initial object so it is first param in list
  queryObject.type = type;
  queryObject.timezone = timezone;

  return queryObject;
}
export const generateSearchDynamicKeyByParams = (params: any) => JSON.stringify(params);

export function parseInputValue(val: string, missingValue: any) {
  if (_.isEmpty(val.trim())) {
    return missingValue;
  }
  return safeParseInt(val, 0);
}

export function getOptionsConfigForPath(filterPath: string, type: string) {
  // TODO: what to do if not set?
  return OPTIONS_CONFIG[type]?.[filterPath];
}

type TLanguageMap = { [key: string]: string | number };

export let LANGUAGE_MAP: TLanguageMap = {};

export const initLanguageMap = (languageMap: TLanguageMap) => {
  LANGUAGE_MAP = languageMap;
};

export const getWowLanguage = (key: string | number, path: string) => LANGUAGE_MAP[`${key}-${path}`];

// OPTIONS_CONFIG is generated from server data, so cannot export it statically
export function getOptionsConfig() {
  return OPTIONS_CONFIG;
}

export const createLanguage = (collection: any, valueAccessor: string, labelAccessor: string, key: string) =>
  _.reduce(
    collection,
    (total, item) => {
      total[`${item[valueAccessor]}-${key}`] = item[labelAccessor];
      return total;
    },
    {} as { [key: string]: string | number }
  );

export let SEARCH_FILTERS: TSearchFilters = {
  character: [],
  guild: [],
  team: [],
  mythic_plus_runs: [],
  raid_encounters: []
};

export const initSearchFilters = (searchFilters: TSearchFilters) => {
  SEARCH_FILTERS = searchFilters;
  processFiltersToAssignIds(SEARCH_FILTERS);
};

export function getSearchFilterForType(type: string) {
  return SEARCH_FILTERS[type];
}

export function processFiltersToAssignIds(config: any) {
  Object.values(config).forEach((subConfig: any) => {
    Object.keys(subConfig).forEach((key: any) => {
      const currentIndex = { index: 0 };
      assignIndex(subConfig[key], currentIndex);
    });
  });
}

export function assignIndex(option: any, currentIndex: { index: number }) {
  option.id = currentIndex.index;
  currentIndex.index += 1;

  if (Array.isArray(option.options)) {
    const localIndex = { index: 0 };
    option.options.forEach((opt: any) => assignIndex(opt, localIndex));
  }
}
const FILTER_TYPE_DESCRIPTION_KEYS: Record<SearchFiltersType, string> = {
  [SearchFiltersType.Character]: 'advanced-search:filter-descriptions.character',
  [SearchFiltersType.Guild]: 'advanced-search:filter-descriptions.guild',
  [SearchFiltersType.Team]: 'advanced-search:filter-descriptions.team',
  [SearchFiltersType.MythicPlusRuns]: 'advanced-search:filter-descriptions.mythic_plus_runs',
  [SearchFiltersType.RaidEncounters]: 'advanced-search:filter-descriptions.raid_encounters'
};

export function getDescriptionForSearchFilterType(type: SearchFiltersType) {
  const key = FILTER_TYPE_DESCRIPTION_KEYS[type] || 'advanced-search:filter-descriptions.default';
  return t(key);
}

/**
 * Check if a dungeon is accessible in a given season
 */
export function isDungeonAccessibleInSeason(
  dungeon: { expansion_id: number; patch: string; id: number },
  season: { allowedDungeons?: number[]; allowedDungeonPatches?: string[]; expansionId?: number; expansion_id?: number }
): boolean {
  // Check if dungeon is explicitly allowed by ID
  if (season.allowedDungeons?.includes(dungeon.id)) {
    return true;
  }
  // Check by expansion and patch
  const expansionId = season.expansionId ?? season.expansion_id;
  return (
    dungeon.expansion_id === expansionId &&
    (!season.allowedDungeonPatches || season.allowedDungeonPatches.includes(dungeon.patch))
  );
}

/**
 * Get set of accessible dungeon IDs for a given season
 */
export function getAccessibleDungeonIdsForSeason(
  seasonSlug: string | null,
  siteMeta: { seasons?: any[]; dungeons?: any[] } | null
): Set<number> | null {
  if (!seasonSlug || !siteMeta?.seasons || !siteMeta?.dungeons) {
    return null;
  }

  const season = siteMeta.seasons.find((s: any) => s.slug === seasonSlug);
  if (!season) {
    return null;
  }

  const accessibleDungeons = siteMeta.dungeons.filter((d: any) => isDungeonAccessibleInSeason(d, season));
  return new Set(accessibleDungeons.map((d: any) => d.id));
}

/**
 * Get set of affix IDs used in a given season.
 * Uses season config's affixLevelRanges for modern seasons (TWW+) and
 * affixSchedules from siteMeta for older seasons.
 */
export function getAccessibleAffixIdsForSeason(
  seasonSlug: string | null,
  siteMeta: { seasons?: any[]; affixSchedules?: any } | null
): Set<number> | null {
  if (!seasonSlug) {
    return null;
  }

  const gameConfig = getGameConfig();
  const seasonDef = gameConfig.getSeasonBySlug(seasonSlug);

  // getSeasonBySlug falls back to first season if not found - check for exact match
  if (!seasonDef || seasonDef.slug !== seasonSlug) {
    return null;
  }

  const affixIds = new Set<number>();

  // Modern seasons: use affixLevelRanges from season config
  if (seasonDef.affixLevelRanges) {
    const permutations = generateAllAffixPermutations(seasonDef.affixLevelRanges);
    for (const perm of permutations) {
      for (const affixId of perm) {
        affixIds.add(affixId);
      }
    }
  }

  // Older seasons: use affix schedules from siteMeta
  if (siteMeta?.affixSchedules?.[seasonDef.expansionId]) {
    const schedules = siteMeta.affixSchedules[seasonDef.expansionId].filter(
      (afx: any) => !seasonDef.affixId || afx.affixes[afx.affixes.length - 1].id === seasonDef.affixId
    );
    for (const schedule of schedules) {
      for (const affix of schedule.affixes) {
        affixIds.add(affix.id);
      }
    }
  }

  // Add seasonal affix if present
  if (seasonDef.affixId) {
    affixIds.add(seasonDef.affixId);
  }

  return affixIds.size > 0 ? affixIds : null;
}

/**
 * Get effective season slug from applied filters or default to current season
 */
export function getEffectiveSeasonSlug(
  appliedFilters: any[],
  currentSeasonSlug: string | null | undefined
): string | null {
  const seasonFilter = appliedFilters.find((f: any) => f.filterPath === 'season');
  return seasonFilter?.value?.eq || currentSeasonSlug || null;
}
